import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import cookieIcon from "@/assets/cookie.svg";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export const CookieConsent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const hasConsent = localStorage.getItem("cookieConsent");
    if (!hasConsent) {
      // Show banner after a short delay
      setTimeout(() => setIsOpen(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem("cookieConsent", JSON.stringify(allAccepted));
    setIsOpen(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem("cookieConsent", JSON.stringify(preferences));
    setIsOpen(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem("cookieConsent", JSON.stringify(onlyNecessary));
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed left-2 right-2 sm:left-4 sm:right-auto bottom-2 sm:bottom-4 z-50 animate-in slide-in-from-bottom sm:slide-in-from-left duration-500">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full sm:w-[380px] max-w-full">
        {!showCustomize ? (
          // Initial Banner
          <div className="p-4 sm:p-6">
            <div className="flex items-start gap-3 sm:gap-4 mb-4">
              <img src={cookieIcon} alt="Cookie" className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-base sm:text-lg mb-2 text-foreground">
                  We value your privacy
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col gap-2 mt-4 sm:mt-6">
              <Button 
                onClick={handleAcceptAll}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
              >
                Accept All
              </Button>
              <Button 
                onClick={handleRejectAll}
                variant="outline"
                className="w-full"
              >
                Reject All
              </Button>
              <Button 
                onClick={() => setShowCustomize(true)}
                variant="ghost"
                className="w-full"
              >
                Customize Preferences
              </Button>
            </div>
          </div>
        ) : (
          // Customization Panel
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <img src={cookieIcon} alt="Cookie" className="w-8 h-8 sm:w-10 sm:h-10" />
                <h3 className="font-bold text-base sm:text-lg text-foreground">
                  Cookie Preferences
                </h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 sm:space-y-6">
              {/* Necessary Cookies */}
              <div className="flex items-start justify-between gap-3 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm sm:text-base text-foreground mb-1">
                    Necessary Cookies
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Essential for the website to function properly. Cannot be disabled.
                  </p>
                </div>
                <Switch
                  checked={preferences.necessary}
                  disabled
                  className="mt-1 flex-shrink-0"
                />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between gap-3 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm sm:text-base text-foreground mb-1">
                    Analytics Cookies
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Help us understand how visitors interact with our website.
                  </p>
                </div>
                <Switch
                  checked={preferences.analytics}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, analytics: checked })
                  }
                  className="mt-1 flex-shrink-0"
                />
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start justify-between gap-3 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm sm:text-base text-foreground mb-1">
                    Marketing Cookies
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Used to track visitors across websites for advertising purposes.
                  </p>
                </div>
                <Switch
                  checked={preferences.marketing}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, marketing: checked })
                  }
                  className="mt-1 flex-shrink-0"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mt-4 sm:mt-6">
              <Button 
                onClick={handleSavePreferences}
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
              >
                Save Preferences
              </Button>
              <Button 
                onClick={() => setShowCustomize(false)}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
